import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React from "react";
import { FC } from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListPaginationStyles {
    select?: SelectPresentationProps;
    perPageStyles?: InjectableCss;
}

export const paginationStyles: ProductListPaginationStyles = {
    select: {
        labelProps: {
            css: css`
                width: unset;
            `,
        },
        cssOverrides: {
            formField: css`
                width: 325px;
                margin-right: 32px;
                label {
                    padding-top: 2px !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-right: 0px;
                        `,
                        css`
                            margin-right: 0px;
                        `,
                        css`
                            margin-right: 0px;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
    perPageStyles: {
        css: css`
            display: inline-flex;
            > label {
                width: 76px;
                padding-right: 11px;
                display: inline-flex;
                align-items: center;
                color: ${ADIColors.darkBlack3};
                font-size: 14px;
                font-weight: 800;
                // For ADINA-57705 Per Page alignment
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 63px;
                            padding-right: 0px;
                        `,
                        null,
                        null,
                        null,
                        null,
                ])}
            }
            > div[class^="FormFieldStyle"] {
                width: 73px;
                > div:first-of-type {
                    width: 0 !important;
                }
                > div[class^="FormInputWrapper"] {
                    > select {
                        width: 75px;
                    }
                    > span {
                        left: 35px;
                    }
                }
            }
        `,
    },
};

const styles = paginationStyles;

export const ADIProductListPageSize: FC<Props> = ({ pagination, addProductFilters }) => {
    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductFilters({ pageSize: newPageSize });
    };

    if (!pagination) {
        return null;
    }

    return (
        <StyledWrapper {...styles.perPageStyles}>
            <label htmlFor="pageSizeSelector">Per Page</label>
            <Select
                {...styles.select}
                value={pagination.pageSize}
                id="pageSizeSelector"
                labelPosition="left"
                onChange={changeResultsPerPage}
                data-test-selector="productListSortSelect"
            >
                {pagination.pageSizeOptions
                    .filter(option => option <= 36)
                    .map(options => (
                        <option key={options} value={options}>
                            {options}
                        </option>
                    ))}
            </Select>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ADIProductListPageSize),
    definition: {
        group: "Basic",
        icon: "Image",
        displayName: "Product List Page Size",
        fieldDefinitions: [],
    },
};

export default widgetModule;
